/**
 * Role-Based Access Control (RBAC) Utilities
 * Manages permissions and access control
 */

import type { UserDTO, UserRole } from '@/common/stores';

/**
 * Permission definition
 */
export interface Permission {
  name: string;
  description: string;
  roles: UserRole[];
}

/**
 * All available permissions in the application
 */
export const PERMISSIONS = {
  // User permissions
  VIEW_USER_PROFILE: {
    name: 'view_user_profile',
    description: 'View own user profile',
    roles: ['USER', 'MEMBER', 'ADMIN'],
  },
  EDIT_USER_PROFILE: {
    name: 'edit_user_profile',
    description: 'Edit own user profile',
    roles: ['USER', 'MEMBER', 'ADMIN'],
  },
  CHANGE_PASSWORD: {
    name: 'change_password',
    description: 'Change own password',
    roles: ['USER', 'MEMBER', 'ADMIN'],
  },
  MANAGE_SUBSCRIPTIONS: {
    name: 'manage_subscriptions',
    description: 'View and manage own subscriptions',
    roles: ['USER', 'MEMBER', 'ADMIN'],
  },

  // Member permissions (elevated)
  ACCESS_PREMIUM_FEATURES: {
    name: 'access_premium_features',
    description: 'Access premium features',
    roles: ['MEMBER', 'ADMIN'],
  },

  // Admin permissions
  MANAGE_USERS: {
    name: 'manage_users',
    description: 'Create, read, update, delete users',
    roles: ['ADMIN'],
  },
  MANAGE_PLANS: {
    name: 'manage_plans',
    description: 'Manage subscription plans',
    roles: ['ADMIN'],
  },
  VIEW_ANALYTICS: {
    name: 'view_analytics',
    description: 'View platform analytics',
    roles: ['ADMIN'],
  },
  MANAGE_PERMISSIONS: {
    name: 'manage_permissions',
    description: 'Manage user roles and permissions',
    roles: ['ADMIN'],
  },
} as const;

/**
 * Role hierarchy and permissions mapping
 */
export const ROLE_PERMISSIONS: Record<UserRole, string[]> = {
  USER: [
    PERMISSIONS.VIEW_USER_PROFILE.name,
    PERMISSIONS.EDIT_USER_PROFILE.name,
    PERMISSIONS.CHANGE_PASSWORD.name,
    PERMISSIONS.MANAGE_SUBSCRIPTIONS.name,
  ],
  MEMBER: [
    PERMISSIONS.VIEW_USER_PROFILE.name,
    PERMISSIONS.EDIT_USER_PROFILE.name,
    PERMISSIONS.CHANGE_PASSWORD.name,
    PERMISSIONS.MANAGE_SUBSCRIPTIONS.name,
    PERMISSIONS.ACCESS_PREMIUM_FEATURES.name,
  ],
  ADMIN: [
    PERMISSIONS.VIEW_USER_PROFILE.name,
    PERMISSIONS.EDIT_USER_PROFILE.name,
    PERMISSIONS.CHANGE_PASSWORD.name,
    PERMISSIONS.MANAGE_SUBSCRIPTIONS.name,
    PERMISSIONS.ACCESS_PREMIUM_FEATURES.name,
    PERMISSIONS.MANAGE_USERS.name,
    PERMISSIONS.MANAGE_PLANS.name,
    PERMISSIONS.VIEW_ANALYTICS.name,
    PERMISSIONS.MANAGE_PERMISSIONS.name,
  ],
};

/**
 * Check if a role has a specific permission
 */
export function hasPermission(role: UserRole, permissionName: string): boolean {
  return ROLE_PERMISSIONS[role].includes(permissionName);
}

/**
 * Check if a role is at least a certain role level
 * Used for hierarchical permission checks
 */
export function hasRole(userRole: UserRole, requiredRole: UserRole | UserRole[]): boolean {
  if (Array.isArray(requiredRole)) {
    return requiredRole.includes(userRole);
  }
  return userRole === requiredRole;
}

/**
 * Check if user can access a resource
 */
export function canAccess(
  userRole: UserRole | null,
  requiredRoles: UserRole | UserRole[]
): boolean {
  if (!userRole) return false;
  return hasRole(userRole, requiredRoles);
}

/**
 * Check if user has all required permissions
 */
export function hasAllPermissions(role: UserRole, permissions: string[]): boolean {
  return permissions.every((perm) => hasPermission(role, perm));
}

/**
 * Check if user has any of the required permissions
 */
export function hasAnyPermission(role: UserRole, permissions: string[]): boolean {
  return permissions.some((perm) => hasPermission(role, perm));
}

/**
 * Get all permissions for a role
 */
export function getPermissionsForRole(role: UserRole): string[] {
  return ROLE_PERMISSIONS[role];
}

/**
 * Get roles that have a specific permission
 */
export function getRolesWithPermission(permissionName: string): UserRole[] {
  return Object.entries(ROLE_PERMISSIONS)
    .filter(([, permissions]) => permissions.includes(permissionName))
    .map(([role]) => role as UserRole);
}

/**
 * Check if a role is admin
 */
export function isAdmin(role: UserRole): boolean {
  return role === 'ADMIN';
}

/**
 * Collect every role attached to a user across the three shapes the backend
 * may return: the singular `role` field, the `roles` array, and the
 * `userRoles` relation. Mirrors the aggregation used by RoleBasedRoute.
 */
export function getAllUserRoles(user: UserDTO | null | undefined): string[] {
  if (!user) return [];

  const roles: string[] = [];

  if (Array.isArray(user.roles) && user.roles.length > 0) {
    roles.push(...user.roles);
  }

  if (Array.isArray(user.userRoles) && user.userRoles.length > 0) {
    user.userRoles.forEach((ur) => {
      const roleName = ur.role?.name;
      if (roleName && !roles.includes(roleName)) {
        roles.push(roleName);
      }
    });
  }

  if (user.role && !roles.includes(user.role)) {
    roles.push(user.role);
  }

  return roles;
}

/**
 * Whether a user object carries the ADMIN role in any of its role fields.
 * Used to gate web-client sign-in to administrators only (the web portal is
 * admin-only; regular users sign in through the mobile app instead).
 */
export function isAdminUser(user: UserDTO | null | undefined): boolean {
  return getAllUserRoles(user).includes('ADMIN');
}

